import {
  type AppLocale,
  type ClientConfigDto,
  type MeDto,
  resolveLocale,
  type WalletDto,
} from '@spin/shared';
import { create } from 'zustand';

/**
 * Full screens. The game opens at the table (as the reference's PLAY does); "exit" leads to the portal, whose
 * pages (services, invited users, watch) and the live and domino rooms are screens too. Everything else in the
 * game is a dialog over the table (store/ui.ts).
 */
export type Screen =
  | { name: 'table' }
  | { name: 'portal' }
  | { name: 'services' }
  | { name: 'invited' }
  | { name: 'watch'; tab?: 'live' | 'domino' }
  | { name: 'dominoRoom'; id: string }
  | { name: 'liveStart' }
  | { name: 'liveRoom'; id: string };

export type ClientConfig = ClientConfigDto;

interface SessionState {
  status: 'booting' | 'login' | 'ready' | 'error';
  error: string | null;
  me: MeDto | null;
  config: ClientConfig | null;
  stack: Screen[];
  /** Another device took over the game session. */
  replaced: boolean;
  banned: boolean;
  setMe: (me: MeDto) => void;
  setBalance: (balance: number) => void;
  setWallet: (wallet: WalletDto) => void;
  setConfig: (config: ClientConfig) => void;
  setStatus: (status: SessionState['status'], error?: string | null) => void;
  go: (screen: Screen) => void;
  back: () => void;
  reset: (screen: Screen) => void;
  /** Replaces the current screen (e.g. the Watch page remembers its tab for the way back). */
  replace: (screen: Screen) => void;
  setReplaced: (replaced: boolean) => void;
  setBanned: () => void;
}

export const useSession = create<SessionState>((set) => ({
  status: 'booting',
  error: null,
  me: null,
  config: null,
  stack: [{ name: 'table' }],
  replaced: false,
  banned: false,
  setMe: (me) => set({ me }),
  setBalance: (balance) => set((s) => (s.me ? { me: { ...s.me, balance } } : {})),
  setWallet: ({ balance, gm }) => set((s) => (s.me ? { me: { ...s.me, balance, gm } } : {})),
  setConfig: (config) => set({ config }),
  setStatus: (status, error = null) => set({ status, error }),
  go: (screen) => set((s) => ({ stack: [...s.stack, screen] })),
  back: () => set((s) => ({ stack: s.stack.length > 1 ? s.stack.slice(0, -1) : s.stack })),
  reset: (screen) => set({ stack: [screen] }),
  replace: (screen) => set((s) => ({ stack: [...s.stack.slice(0, -1), screen] })),
  setReplaced: (replaced) => set({ replaced }),
  setBanned: () => set({ banned: true }),
}));

export function currentScreen(): Screen {
  const { stack } = useSession.getState();
  return stack[stack.length - 1]!;
}

export function useLocale(fallback?: string): AppLocale {
  const me = useSession((s) => s.me);
  return me?.locale ?? resolveLocale(fallback ?? navigator.language);
}
